import { HttpMethod, tryFetch, type HttpResponse } from '@/modules/services/http'
import type { Validation, Shipment } from '@/shared/models/shared.model'
import type { MotionStatusErrorCode } from '../types/motion.enums'
import type {
  EncryptedKeyValue,
  ExternalKey,
  Attachment,
  MotionViewStatus,
  ShipmentDate,
  Reference,
  GeneralInfo,
  GeneralHistory,
  Dimension,
  SendTo,
  Carrier,
  CarrierAdmin,
  CarrierDelivery,
  CarrierHistory,
  ShipmentInfo,
  SendFrom,
  DimensionHistory,
  Recipient,
  AdditionalInfo,
  OrderInfo,
  OrderDelivery,
  ShipmentDelivery,
  ShipmentDeliveryHistory,
  OrderDeliveryHistory,
  ShipmentSpecification,
} from '../models/motion.model'
import type { MotionBoxErrorCode } from '@/modules/motion-box/types/motion-box.enums'
import type { MotionBoxStatusRequest, NotificationRequest } from './motion.requests'
import type { Filter } from '@/modules/filter/models/filter.model'

const BASE_URL = import.meta.env.VITE_MAIN_BASE_URL

const getGeneralInfo = async (
  mBoxId: number,
): Promise<HttpResponse<GeneralInfo, MotionBoxErrorCode>> =>
  await tryFetch<GeneralInfo, MotionBoxErrorCode>(
    HttpMethod.Get,
    BASE_URL,
    ['motion-boxes', mBoxId, 'info'],
    {
      view: true,
    },
  )

const getGeneralHistory = async (
  mBoxId: number,
): Promise<HttpResponse<GeneralHistory, MotionBoxErrorCode>> =>
  await tryFetch<GeneralHistory, MotionBoxErrorCode>(
    HttpMethod.Get,
    BASE_URL,
    ['motion-boxes', mBoxId, 'history'],
    {
      view: true,
    },
  )

const getAdditionalInfo = async (
  mBoxId: number,
): Promise<HttpResponse<AdditionalInfo, MotionBoxErrorCode>> =>
  await tryFetch<AdditionalInfo, MotionBoxErrorCode>(
    HttpMethod.Get,
    BASE_URL,
    ['motion-boxes', mBoxId, 'additional-info'],
    {
      view: true,
    },
  )

const getDimension = async (mBoxId: number): Promise<HttpResponse<Dimension, MotionBoxErrorCode>> =>
  await tryFetch<Dimension, MotionBoxErrorCode>(
    HttpMethod.Get,
    BASE_URL,
    ['motion-boxes', mBoxId, 'dimension'],
    {
      view: true,
    },
  )

const getDimensionHistory = async (
  mBoxId: number,
): Promise<HttpResponse<DimensionHistory, MotionBoxErrorCode>> =>
  await tryFetch<DimensionHistory, MotionBoxErrorCode>(
    HttpMethod.Get,
    BASE_URL,
    ['motion-boxes', mBoxId, 'dimension-history'],
    {
      view: true,
    },
  )

const getSendFrom = async (mBoxId: number): Promise<HttpResponse<SendFrom, MotionBoxErrorCode>> =>
  await tryFetch<SendFrom, MotionBoxErrorCode>(
    HttpMethod.Get,
    BASE_URL,
    ['motion-boxes', mBoxId, 'send-from'],
    {
      view: true,
    },
  )

const getSendTo = async (mBoxId: number): Promise<HttpResponse<SendTo, MotionBoxErrorCode>> =>
  await tryFetch<SendTo, MotionBoxErrorCode>(
    HttpMethod.Get,
    BASE_URL,
    ['motion-boxes', mBoxId, 'send-to'],
    {
      view: true,
    },
  )

const getCarrier = async (
  mBoxId: number,
): Promise<HttpResponse<Carrier | null, MotionBoxErrorCode>> =>
  await tryFetch<Carrier | null, MotionBoxErrorCode>(
    HttpMethod.Get,
    BASE_URL,
    ['motion-boxes', mBoxId, 'carrier'],
    {
      view: true,
    },
  )

const getCarrierAdmin = async (
  mBoxId: number,
): Promise<HttpResponse<CarrierAdmin | null, MotionBoxErrorCode>> =>
  await tryFetch<CarrierAdmin | null, MotionBoxErrorCode>(
    HttpMethod.Get,
    BASE_URL,
    ['admin', 'motion-boxes', mBoxId, 'carrier'],
    {
      view: true,
    },
  )

const getCarrierDelivery = async (
  mBoxId: number,
): Promise<HttpResponse<CarrierDelivery | null, MotionBoxErrorCode>> =>
  await tryFetch<CarrierDelivery | null, MotionBoxErrorCode>(
    HttpMethod.Get,
    BASE_URL,
    ['motion-boxes', mBoxId, 'carrier-delivery'],
    {
      view: true,
    },
  )

const getCarrierHistory = async (
  mBoxId: number,
): Promise<HttpResponse<CarrierHistory | null, MotionBoxErrorCode>> =>
  await tryFetch<CarrierHistory | null, MotionBoxErrorCode>(
    HttpMethod.Get,
    BASE_URL,
    ['motion-boxes', mBoxId, 'carrier-history'],
    {
      view: true,
    },
  )

const getCarrierSetting = async (
  mBoxId: number,
): Promise<HttpResponse<CarrierHistory | null, MotionBoxErrorCode>> =>
  await tryFetch<CarrierHistory | null, MotionBoxErrorCode>(
    HttpMethod.Get,
    BASE_URL,
    ['motion-boxes', mBoxId, 'carrier-setting'],
    {
      view: true,
    },
  )

const getOrderInfo = async (
  mBoxId: number,
): Promise<HttpResponse<OrderInfo | null, MotionBoxErrorCode>> =>
  await tryFetch<OrderInfo | null, MotionBoxErrorCode>(
    HttpMethod.Get,
    BASE_URL,
    ['motion-boxes', mBoxId, 'order-info'],
    {
      view: true,
    },
  )

const getOrderDelivery = async (
  mBoxId: number,
): Promise<HttpResponse<OrderDelivery | null, MotionBoxErrorCode>> =>
  await tryFetch<OrderDelivery | null, MotionBoxErrorCode>(
    HttpMethod.Get,
    BASE_URL,
    ['motion-boxes', mBoxId, 'order-delivery'],
    {
      view: true,
    },
  )

const getOrderDeliveryHistory = async (
  mBoxId: number,
): Promise<HttpResponse<OrderDeliveryHistory | null, MotionBoxErrorCode>> =>
  await tryFetch<OrderDeliveryHistory | null, MotionBoxErrorCode>(
    HttpMethod.Get,
    BASE_URL,
    ['motion-boxes', mBoxId, 'order-delivery-history'],
    {
      view: true,
    },
  )

const getShipmentInfo = async (
  mBoxId: number,
): Promise<HttpResponse<ShipmentInfo | null, MotionBoxErrorCode>> =>
  await tryFetch<ShipmentInfo | null, MotionBoxErrorCode>(
    HttpMethod.Get,
    BASE_URL,
    ['motion-boxes', mBoxId, 'shipment-info'],
    {
      view: true,
    },
  )

const getShipmentDelivery = async (
  mBoxId: number,
): Promise<HttpResponse<ShipmentDelivery | null, MotionBoxErrorCode>> =>
  await tryFetch<ShipmentDelivery | null, MotionBoxErrorCode>(
    HttpMethod.Get,
    BASE_URL,
    ['motion-boxes', mBoxId, 'shipment-delivery'],
    {
      view: true,
    },
  )

const getShipmentDeliveryHistory = async (
  mBoxId: number,
): Promise<HttpResponse<ShipmentDeliveryHistory | null, MotionBoxErrorCode>> =>
  await tryFetch<ShipmentDeliveryHistory | null, MotionBoxErrorCode>(
    HttpMethod.Get,
    BASE_URL,
    ['motion-boxes', mBoxId, 'shipment-delivery-history'],
    {
      view: true,
    },
  )

const getShipmentSpecification = async (
  mBoxId: number,
): Promise<HttpResponse<ShipmentSpecification | null, MotionBoxErrorCode>> =>
  await tryFetch<ShipmentSpecification | null, MotionBoxErrorCode>(
    HttpMethod.Get,
    BASE_URL,
    ['motion-boxes', mBoxId, 'shipment-specification'],
    {
      view: true,
    },
  )

const getRecipient = async (
  mBoxId: number,
): Promise<HttpResponse<Recipient | null, MotionBoxErrorCode>> =>
  await tryFetch<Recipient | null, MotionBoxErrorCode>(
    HttpMethod.Get,
    BASE_URL,
    ['motion-boxes', mBoxId, 'recipient'],
    {
      view: true,
    },
  )

const getPayer = async (
  mBoxId: number,
): Promise<HttpResponse<Recipient | null, MotionBoxErrorCode>> =>
  await tryFetch<Recipient | null, MotionBoxErrorCode>(
    HttpMethod.Get,
    BASE_URL,
    ['motion-boxes', mBoxId, 'recipient', 'payer'],
    {
      view: true,
    },
  )

const getBoxes = async (
  body: Filter,
): Promise<{ data?: any | null; error?: any; status: number }> =>
  await tryFetch(HttpMethod.Post, BASE_URL, ['motion-boxes'], {
    view: true,
    body,
  })

const getBoxesByShipmentNo = async (
  shipmentNo: string,
): Promise<{ data?: any | null; error?: any; status: number }> =>
  await tryFetch(HttpMethod.Get, BASE_URL, ['motion-boxes', shipmentNo], {
    view: true,
  })

const getStatuses = async (): Promise<
  HttpResponse<MotionViewStatus[] | null, MotionBoxErrorCode>
> =>
  await tryFetch<MotionViewStatus[] | null, MotionBoxErrorCode>(
    HttpMethod.Get,
    BASE_URL,
    ['motion', 'status'],
    {
      view: true,
    },
  )

const getStatusValidation = async (body: number[]): Promise<HttpResponse<Validation | null, any>> =>
  await tryFetch<Validation | null, any>(
    HttpMethod.Post,
    BASE_URL,
    ['motion-boxes', 'validate-status'],
    {
      view: true,
      body,
    },
  )

const getExternalKey = async (
  mBoxId: number,
): Promise<HttpResponse<ExternalKey, MotionBoxErrorCode>> =>
  await tryFetch<ExternalKey, MotionBoxErrorCode>(
    HttpMethod.Get,
    BASE_URL,
    ['motion-boxes', mBoxId, 'external-key'],
    {
      view: true,
    },
  )

const getAdminExternalKey = async (
  mBoxId: number,
): Promise<HttpResponse<ExternalKey, MotionBoxErrorCode>> =>
  await tryFetch<ExternalKey, MotionBoxErrorCode>(
    HttpMethod.Get,
    BASE_URL,
    ['admin', 'motion-boxes', mBoxId, 'external-key'],
    {
      view: true,
    },
  )

const getDeliveryNote = async (
  mBoxId: number,
): Promise<HttpResponse<EncryptedKeyValue, MotionBoxErrorCode>> =>
  await tryFetch<EncryptedKeyValue, MotionBoxErrorCode>(
    HttpMethod.Get,
    BASE_URL,
    ['motion-boxes', mBoxId, 'delivery-note'],
    {
      view: true,
    },
  )

const getAdminDeliveryNote = async (
  mBoxId: number,
): Promise<HttpResponse<EncryptedKeyValue, MotionBoxErrorCode>> =>
  await tryFetch<EncryptedKeyValue, MotionBoxErrorCode>(
    HttpMethod.Get,
    BASE_URL,
    ['admin', 'motion-boxes', mBoxId, 'delivery-note'],
    {
      view: true,
    },
  )

const getDimensionValueIds = async (
  mBoxId: number,
): Promise<HttpResponse<Record<number, number>, MotionBoxErrorCode>> =>
  await tryFetch<Record<number, number>, MotionBoxErrorCode>(
    HttpMethod.Get,
    BASE_URL,
    ['motion-boxes', mBoxId, 'dimension-ids'],
    {
      view: true,
    },
  )

const getShipmentDate = async (
  mBoxId: number,
): Promise<HttpResponse<ShipmentDate, MotionBoxErrorCode>> =>
  await tryFetch<ShipmentDate, MotionBoxErrorCode>(
    HttpMethod.Get,
    BASE_URL,
    ['motion-boxes', mBoxId, 'shipment-date'],
    {
      view: true,
    },
  )

const getReference = async (mBoxId: number): Promise<HttpResponse<Reference, MotionBoxErrorCode>> =>
  await tryFetch<Reference, MotionBoxErrorCode>(
    HttpMethod.Get,
    BASE_URL,
    ['motion-boxes', mBoxId, 'reference'],
    {
      view: true,
    },
  )

const getUpdatedBoxes = async (
  body: any,
): Promise<{ data?: any | null; error?: any; status: number }> =>
  await tryFetch(HttpMethod.Post, BASE_URL, ['motion-view', 'update'], {
    view: true,
    body,
  })

const searchBoxes = async (
  body: any,
): Promise<{ data?: any | null; error?: any; status: number }> =>
  await tryFetch(HttpMethod.Post, BASE_URL, ['search', 'boxes'], { view: true, body })

const getSelectedBox = async (
  mBoxId: number,
): Promise<{ data?: any | null; error?: any; status: number }> =>
  await tryFetch(HttpMethod.Get, BASE_URL, ['motion-view', 'mbox', mBoxId, 'select'], {
    view: true,
  })

const getRelatedMotionBox = async (
  mBoxId: number,
): Promise<{ data?: any | null; error?: any; status: number }> =>
  await tryFetch(HttpMethod.Get, BASE_URL, ['motion-boxes', mBoxId, 'relations'], { view: true })

const updateStatus = async (
  mBoxId: number,
  body: MotionBoxStatusRequest,
): Promise<HttpResponse<Shipment | null, MotionStatusErrorCode>> =>
  await tryFetch<Shipment | null, MotionStatusErrorCode>(
    HttpMethod.Patch,
    BASE_URL,
    ['motion-boxes', mBoxId, 'status'],
    {
      view: true,
      body,
    },
  )

const updateDimensions = async (
  mBoxId: number,
  body: Record<number, number>,
): Promise<HttpResponse<Shipment | null, MotionBoxErrorCode>> =>
  await tryFetch<Shipment | null, MotionBoxErrorCode>(
    HttpMethod.Patch,
    BASE_URL,
    ['motion-boxes', mBoxId, 'dimensions'],
    {
      view: true,
      body,
    },
  )

const updateShipmentDate = async (
  mBoxId: number,
  body: ShipmentDate,
): Promise<HttpResponse<Shipment | null, MotionBoxErrorCode>> =>
  await tryFetch<Shipment | null, MotionBoxErrorCode>(
    HttpMethod.Patch,
    BASE_URL,
    ['motion-boxes', mBoxId, 'shipment-date'],
    {
      view: true,
      body,
    },
  )

const updateReference = async (
  mBoxId: number,
  body: Reference,
): Promise<HttpResponse<Shipment | null, MotionBoxErrorCode>> =>
  await tryFetch<Shipment | null, MotionBoxErrorCode>(
    HttpMethod.Patch,
    BASE_URL,
    ['motion-boxes', mBoxId, 'reference'],
    {
      view: true,
      body,
    },
  )

const uploadFile = async (
  mBoxId: number,
  body: FormData,
): Promise<HttpResponse<Attachment, MotionBoxErrorCode>> =>
  await tryFetch<Attachment, MotionBoxErrorCode>(
    HttpMethod.Post,
    BASE_URL,
    ['motion-boxes', mBoxId, 'files'],
    {
      view: true,
      body,
    },
  )

const createNotification = async (
  mBoxId: number,
  body: NotificationRequest,
): Promise<HttpResponse<Shipment | null, MotionBoxErrorCode>> =>
  await tryFetch<Shipment | null, MotionBoxErrorCode>(
    HttpMethod.Post,
    BASE_URL,
    ['motion-boxes', mBoxId, 'notification'],
    {
      view: true,
      body,
    },
  )

export const motionViewApi = {
  getGeneralInfo,
  getGeneralHistory,
  getAdditionalInfo,
  getDimension,
  getDimensionHistory,
  getSendTo,
  getSendFrom,
  getRecipient,
  getPayer,
  getCarrier,
  getCarrierAdmin,
  getCarrierSetting,
  getCarrierDelivery,
  getCarrierHistory,
  getOrderInfo,
  getOrderDelivery,
  getOrderDeliveryHistory,
  getShipmentInfo,
  getShipmentDelivery,
  getShipmentDeliveryHistory,
  getShipmentSpecification,
  getBoxes,
  getBoxesByShipmentNo,
  getUpdatedBoxes,
  getStatusValidation,
  getStatuses,
  getExternalKey,
  getAdminExternalKey,
  getDeliveryNote,
  getAdminDeliveryNote,
  getDimensionValueIds,
  getShipmentDate,
  getReference,
  uploadFile,
  updateStatus,
  updateDimensions,
  updateShipmentDate,
  updateReference,
  createNotification,
  searchBoxes,
  getSelectedBox,
  getRelatedMotionBox,
}
